import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, SlidersHorizontal, Search, X, Home } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SiteCard from '../components/SiteCard';
import { Container } from '../components/styled/Container';
import { PageLayout } from '../components/styled/PageLayout';
import { fetchCategories, fetchSites } from '../store/actions/siteActions';
import { useLocale } from '../context/LocaleContext';
import SeoHead from '../components/SeoHead';
import { useT } from '../hooks/useT';
import AdSidebar from '../components/ads/AdSidebar';
import AdInline from '../components/ads/AdInline';
import { sidebarAds, cardAds } from '../data/adData';
import styled from 'styled-components';

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-bottom: 1.5rem;
  transition: color 0.2s;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const TitleQuery = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

const Count = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.5rem 0 0 0;
`;

const FiltersRow = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const FilterLabel = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FilterBtn = styled.button`
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 500;
  transition: all 0.2s;
  background: ${({ $active, theme }) => ($active ? theme.colors.maroon : theme.colors.muted)};
  color: ${({ $active, theme }) => ($active ? theme.colors.maroonForeground : theme.colors.foreground)};
  &:hover {
    opacity: 0.9;
  }
`;

const SortGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SortBtn = styled.button`
  padding: 0.375rem 0.75rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 600;
  text-transform: capitalize;
  transition: all 0.2s;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.muted)};
  color: ${({ $active, theme }) => ($active ? theme.colors.primaryForeground : theme.colors.foreground)};
`;

const ClearBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 500;
  background: ${({ theme }) => theme.colors.muted};
  color: ${({ theme }) => theme.colors.foreground};
  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primaryForeground};
  }
`;

const ResultsRow = styled.div`
  margin-top: 2rem;
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;

const ResultsMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const AdSidebarCol = styled.aside`
  display: none;
  @media (min-width: 1024px) {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 16rem;
    flex-shrink: 0;
  }
`;

const EmptyWrap = styled.div`
  text-align: center;
  padding: 5rem 0;
`;

const EmptyIcon = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const EmptyTitle = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.xl};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const EmptyDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.5rem 0 0 0;
`;

function SearchResults() {
  const dispatch = useDispatch();
  const { prependPath } = useLocale();
  const t = useT();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  /** Category filter: slug (matches API `?category=` resolution). */
  const [activeCategorySlug, setActiveCategorySlug] = useState(null);
  const [sortBy, setSortBy] = useState('relevance');
  const { items, categories, pagination, loading, error } = useSelector((state) => state.sites);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchSites({ q: query, category: activeCategorySlug || undefined, sort: sortBy, limit: 50 }));
  }, [dispatch, query, activeCategorySlug, sortBy]);

  const hasActiveFilters = activeCategorySlug || sortBy !== 'relevance';
  const clearFilters = () => {
    setActiveCategorySlug(null);
    setSortBy('relevance');
  };
  const clearSearch = () => navigate(prependPath('/'));
  const sortOptions = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'userScore', label: 'User score' },
    { value: 'alternativeRank', label: 'Alt. rank' },
  ];

  return (
    <PageLayout>
      <SeoHead
        title={query ? `Search: ${query} | CiteMatch` : 'Search | CiteMatch'}
        description={query ? `Search results for "${query}" on CiteMatch.` : 'Search CiteMatch for similar websites and alternatives.'}
        noindex
      />
      <Navbar />
      <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <BackLink to={prependPath('/')}>
          <ArrowLeft size={16} strokeWidth={2.5} /> {t('common.back')}
        </BackLink>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Title>
            Results for <TitleQuery>"{query}"</TitleQuery>
          </Title>
          <Count>{(pagination?.total ?? items.length) || 0} sites found</Count>
        </motion.div>

        <FiltersRow>
          <FilterGroup>
            <FilterLabel>
              <SlidersHorizontal size={16} strokeWidth={2.5} /> Filter:
            </FilterLabel>
            <FilterBtn $active={!activeCategorySlug} onClick={() => setActiveCategorySlug(null)}>All</FilterBtn>
            {categories.slice(0, 6).map((cat) => (
              <FilterBtn
                key={cat.slug || cat._id || cat.name}
                $active={activeCategorySlug === cat.slug}
                onClick={() =>
                  setActiveCategorySlug(activeCategorySlug === cat.slug ? null : cat.slug)
                }
              >
                {cat.name}
              </FilterBtn>
            ))}
          </FilterGroup>
          <SortGroup>
            <span style={{ fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>Sort:</span>
            {sortOptions.map((opt) => (
              <SortBtn key={opt.value} $active={sortBy === opt.value} onClick={() => setSortBy(opt.value)}>
                {opt.label}
              </SortBtn>
            ))}
          </SortGroup>
        </FiltersRow>
        {(query || hasActiveFilters) && (
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {hasActiveFilters && (
              <ClearBtn type="button" onClick={clearFilters}>
                <X size={14} /> Clear filters
              </ClearBtn>
            )}
            {query && (
              <ClearBtn type="button" onClick={clearSearch}>
                <Home size={14} /> New search
              </ClearBtn>
            )}
          </div>
        )}

        {error && <div style={{ marginTop: '1rem', color: 'var(--crimson)' }}>{error}</div>}

        <ResultsRow>
          <ResultsMain>
            {loading && items.length === 0 ? null : items.length > 0 ? (
              <>
                <ResultsGrid>
                  {items.slice(0, items.length > 4 ? 4 : items.length).map((site) => (
                    <SiteCard key={site.id} site={site} />
                  ))}
                </ResultsGrid>
                {items.length > 4 && (
                  <>
                    <div style={{ margin: '1.5rem 0' }}>
                      <AdInline ad={cardAds[0]} />
                    </div>
                    <ResultsGrid>
                      {items.slice(4).map((site) => (
                        <SiteCard key={site.id} site={site} />
                      ))}
                    </ResultsGrid>
                  </>
                )}
              </>
            ) : null}
          </ResultsMain>
          <AdSidebarCol aria-label="Sponsored">
            {sidebarAds.map((ad) => (
              <AdSidebar key={ad.id} ad={ad} />
            ))}
          </AdSidebarCol>
        </ResultsRow>

        {!loading && items.length === 0 && (
          <EmptyWrap>
            <EmptyIcon>
              <Search size={24} />
            </EmptyIcon>
            <EmptyTitle>No results found</EmptyTitle>
            <EmptyDesc>Try a different domain or keyword</EmptyDesc>
          </EmptyWrap>
        )}
      </Container>
      <Footer />
    </PageLayout>
  );
}

export default SearchResults;
